import axios from "axios";
import { useCallback, useState } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/orders";

const api = axios.create({
  baseURL: baseUrl,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const useOrders = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAllOrders = useCallback(
    async ({ includeConfigurator = false } = {}) => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get("/", {
          params: includeConfigurator ? { includeConfigurator: true } : {},
        });
        return data;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getAdminOrders = useCallback(
    async (companyId, { includeConfigurator = false } = {}) => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get("/admin", {
          params: {
            companyId,
            ...(includeConfigurator ? { includeConfigurator: true } : {}),
          },
        });
        return data;
      } catch (error) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getOrderById = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/${id}`);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const createOrder = useCallback(async (orderData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.post("/", orderData);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateOrderStatus = useCallback(async (id, status) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.put(`/${id}/status`, { status });
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteOrder = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/${id}`);
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getAllOrders,
    getAdminOrders,
    getOrderById,
    createOrder,
    updateOrderStatus,
    deleteOrder,
  };
};

export default useOrders;
